import { memo, FC, useState, useEffect } from 'react'
import {
  Center,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  SimpleGrid,
  Icon,
  Spinner,
  useDisclosure,
  useColorModeValue,
  Flex,
} from '@chakra-ui/react'

// import { COLORS } from 'lib/constants'
import OUTLINES_ICONS from '../outlineIcons'
import TWO_TONE_ICONS from '../twoToneIcons'

type Props = {
  setValue: any
  getValues: any
  registerIcon: string
  registerColor: string
  title?: string
  variant?: 'default' | 'twoTone'
}

const CustomIconPicker: FC<Props> = ({ setValue, getValues, registerIcon, registerColor, title, variant = 'twoTone' }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedIcon, setSelectedIcon] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [activeIcon, setActiveIcon] = useState('')
  const [activeColor, setActiveColor] = useState('')
  const [loadingComponent, setLoadingComponent] = useState(true)

  const selectInputBGColor = useColorModeValue('gray.50', 'gray.700')
  const borderColorWhenItemIsNotSelected = useColorModeValue('gray.100', 'gray.700')
  const borderColorWhenItemIsSelected = useColorModeValue('black', 'white')

  const ICONS = variant === 'twoTone' ? TWO_TONE_ICONS : OUTLINES_ICONS

  function selectIcon(icon: string) {
    if (icon === selectedIcon) {
      return setSelectedIcon('')
    }
    setSelectedIcon(icon)
  }

  // function selectColor(icon: string) {
  //   setSelectedColor(icon)
  // }

  function onSubmit() {
    setActiveIcon(selectedIcon)
    setActiveColor(selectedColor)
    if (registerIcon) {
      setValue(registerIcon, selectedIcon)
    }
    if (registerColor) {
      setValue(registerColor, selectedColor)
    }
    onClose()
  }

  function getIcon() {
    const filter = ICONS.filter((item) => item.value === activeIcon)
    if (filter.length > 0) return filter[0].icon
  }

  useEffect(() => {
    async function prepareComponent() {
      if (getValues) {
        const values = getValues()
        const defaultIcon = values[registerIcon]
        const defaultColor = values[registerColor]

        if (registerIcon && defaultIcon) {
          setActiveIcon(defaultIcon)
          setSelectedIcon(defaultIcon)
        }
        if (registerColor && defaultColor) {
          setActiveColor(defaultColor)
          setSelectedColor(defaultColor)
        }
      }
      setLoadingComponent(false)
    }
    prepareComponent()
  }, [])

  const RenderActiveIcon = () => {
    if (activeIcon) {
      if (variant === 'twoTone') {
        return <Icon as={getIcon()} variant="twoTone" color="primary" boxSize={12} />
      }
      return <Icon as={getIcon()} color={`${activeColor}.500`} boxSize={10} />
    }
    return <></>
  }

  if (loadingComponent) {
    return (
      <Center border="1px" borderStyle="dashed" borderColor="gray.300" p={4} borderRadius="md" cursor="pointer" h="16" w="16">
        <Spinner />
      </Center>
    )
  }

  return (
    <>
      <Center
        bg="light"
        border="1px"
        borderStyle="dashed"
        borderColor="gray.300"
        p={4}
        borderRadius="md"
        cursor="pointer"
        h="16"
        w="16"
        onClick={onOpen}
        _hover={{ borderColor: 'primary.300', bg: selectInputBGColor }}
      >
        <RenderActiveIcon />
      </Center>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          {title ? <DrawerHeader>{title}</DrawerHeader> : <DrawerHeader py="5" />}

          <DrawerBody>
            <SimpleGrid columns={variant === 'twoTone' ? 5 : 7}>
              {ICONS.map((item) => (
                <Center
                  key={item.value}
                  cursor="pointer"
                  onClick={() => selectIcon(item.value)}
                  border="2px"
                  borderColor={selectedIcon === item.value ? borderColorWhenItemIsSelected : borderColorWhenItemIsNotSelected}
                  borderRadius="md"
                >
                  {variant === 'twoTone' ? (
                    <Icon as={item.icon} variant="twoTone" color="primary" boxSize={16} />
                  ) : (
                    <Icon as={item.icon} color={`${selectedColor}.500`} boxSize={10} />
                  )}
                </Center>
              ))}
            </SimpleGrid>
          </DrawerBody>

          <DrawerFooter justifyContent="space-between">
            <Button variant="ghost" onClick={() => selectIcon('')}>
              Sem ícone
            </Button>

            <Flex align="center" gap={2}>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
              <Button mr={3} onClick={onSubmit}>
                Selecionar
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default memo(CustomIconPicker)
